
import axios from 'axios';
import { apiUrl, apiKey, availableLanguages, thumbHeight, thumbQuality, storageUrl } from './Config';

const apiHeaders = () => {
    if (!apiKey) {
        return {};
    }

    return {
        'api-key': `${apiKey}`,
    };
};

// Only public CMS data belongs here; authentication continues to be fetched separately.
const responseCache = new Map();
const thumbnailCache = new Map();
const selectionCache = new Map();
const CONTENT_TTL = 5 * 60 * 1000;
const DISPLAY_TTL = 30 * 60 * 1000;

const readSelection = (key, maxAge = DISPLAY_TTL) => {
    const entry = selectionCache.get(key);
    if (!entry || Date.now() - entry.savedAt >= maxAge) return null;
    selectionCache.delete(key);
    selectionCache.set(key, entry);
    return entry.value;
};

const saveSelection = (key, value) => {
    selectionCache.delete(key);
    selectionCache.set(key, { value, savedAt: Date.now() });
    if (selectionCache.size > 192) selectionCache.delete(selectionCache.keys().next().value);
};

const groupCacheKey = (language) => `groups:${language?.code}`;
const areaCacheKey = (language, group) => `areas:${language?.code}:${group?.id}`;

// Show the last successful public list immediately while stale content refreshes.
// Access permissions are still calculated separately from the current auth state.
export const getCachedGroups = (language) => language?.code ? readSelection(groupCacheKey(language)) : null;
export const getCachedAreas = (language, group) => language?.code && group?.id ? readSelection(areaCacheKey(language, group)) : null;

const fetchCached = (url, ttl = 5 * 60 * 1000) => {
    const cache = url.startsWith('assets/image/') ? thumbnailCache : responseCache;
    const cached = cache.get(url);
    if (cached && cached.expiresAt > Date.now()) {
        cache.delete(url);
        cache.set(url, cached);
        return cached.promise;
    }

    const entry = { expiresAt: Infinity };
    entry.promise = axios.get(`${apiUrl}${url}`, { headers: apiHeaders(), timeout: 15000 })
        .then((response) => {
            entry.expiresAt = Date.now() + ttl;
            return response.data ?? null;
        })
        .catch((error) => {
            if (cache.get(url) === entry) cache.delete(url);
            throw error;
        });
    cache.set(url, entry);
    if (cache.size > 256) cache.delete(cache.keys().next().value);
    return entry.promise;
};

const toItems = (response) => {
    if (Array.isArray(response)) {
        return response;
    }

    if (!response || typeof response !== 'object') {
        return [];
    }

    if (Array.isArray(response.items)) {
        return response.items;
    }

    if (Array.isArray(response.data)) {
        return response.data;
    }

    if (Array.isArray(response.entries)) {
        return response.entries;
    }

    if (Array.isArray(response.results)) {
        return response.results;
    }

    if (response.data && typeof response.data === 'object') {
        return toItems(response.data);
    }

    return [];
};



/*
    Dle oblasti a typu kvizu vygeneruje nahodne otazky ze slovní zásoby
*/
export const generateQuestionsByAreaAndQuizType = async (area, quiz, level, { deferImages = false } = {}) => {
    // Generate quiz order without fetching media for unseen questions.
    const resolveImage = deferImages ? async (image) => image : getImageThumbnail;

    // Fisher-Yates shuffle pro skutečně náhodné míchání
    const shuffleArray = (array) => {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    };

    const getRandomElements = (array, count) => {
        return shuffleArray(array).slice(0, count);
    };

    // Pomocná funkce pro kontrolu duplicit v poli
    const arrayEquals = (a, b) => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, idx) => val === sortedB[idx]);
    };
    const replaceAsteriskWithNeedle = (str, needle) => {
        needle = '<br />' + needle;
        return str.split('*').join(needle);
    };
    const normalizeString = (str) => {
        return str
            ?.normalize('NFKD')                                // Rozloží kombinované znaky (např. é → e +  ́)
            .replace(/[\u0300-\u036f]/g, '')                   // Odstraní všechny diakritické znaky
            .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035\u0060\u00B4\u02BB\u02BC\u02BD\u02BE\u02BF\u275B\u275C\uFF07]/g, "'") // Všechny varianty apostrofů a uvozovek → '
            .replace(/[^a-zA-Z0-9']/g, ' ')                    // Nepísmenkové znaky na mezeru (volitelné)
            .replace(/\s+/g, ' ')                              // Sjednotí vícenásobné mezery
            .toLowerCase()
            .trim();
    };

    const getImageByWordIndex = (index) => {
        const targetWord = area?.wordsEs?.[index];
        if (!targetWord) return null;

        const normalizedTarget = normalizeString(targetWord);

        // Projdeme klíče v area.images a najdeme shodu přes normalizaci
        const imageKeys = Object.keys(area?.images || {});
        let matchedKey = null;

        for (const key of imageKeys) {
            const normalizedKey = normalizeString(key);
            if (normalizedKey === normalizedTarget) {
                matchedKey = key;
                break;
            }
        }

        if (!matchedKey) {
            console.warn(`🖼️ Obrázek nenalezen pro "${targetWord}"`);
            return null;
        }

        return area.images[matchedKey];
    };

    const getAudioByWord = (word) => {

        // console.log('area.audio', area.audio );
        const audioKeys = Object.keys(area.audio || {});
        const normalizedTarget = normalizeString(word);

        //  console.groupCollapsed(`[🔊 getAudioByWord] Hledám: "${word}" → "${normalizedTarget}"`);
        let matchedKey = null;

        for (const key of audioKeys) {
            const normalizedKey = normalizeString(key);
            const isMatch = normalizedKey === normalizedTarget;
            //   console.log(`${isMatch ? '✅' : '❌'} "${key}" → "${normalizedKey}" vs "${normalizedTarget}"`);
            if (isMatch) {
                matchedKey = key;
                break;
            }
        }

        if (!matchedKey) {
            console.warn(`⚠️ Audio nenalezeno pro "${word}"`);
        } else {
            //     console.log(`🎯 Shoda: "${matchedKey}"`);
        }
        console.groupEnd();

        const path = matchedKey ? area.audio[matchedKey]?.path : null;
        return path ? storageUrl + path : null;
    };

    switch (quiz.type) {
        case 'audioSelect': {
            // Počet otázek (např. 10)
            const NUM_QUESTIONS = 1;
            // Všechna zvířata v oblasti
            const allWords = area.words;
            // Obrázky všech zvířat
            const allImages = await Promise.all(
                allWords.map((w, idx) => resolveImage(getImageByWordIndex(idx)))
            );
            // Nahrávka obsahující slova (audio10)
            const audio = area.audio10?.path ? storageUrl + area.audio10.path : null;
            // Správné odpovědi (answers10)
            const correctAnswers = Array.isArray(area.answers10) ? area.answers10 : [];
            // Vygeneruj otázky
            const questions = Array.from({ length: NUM_QUESTIONS }).map((_, i) => ({
                id: i + 1,
                audio,
                options: allImages, // obrázky všech zvířat
                optionsWords: allWords, // slova ke všem obrázkům
                correctAnswers, // pole slovíček, která zazněla
                userAnswer: [], // uživatel může vybrat více obrázků
                status: null
            }));
            return questions;
        }
        case 'listen3': {
            const NUM_OPTIONS = 3; // počet koláží
            const NUM_SOUNDS = 3;  // počet zvuků/obrázků v jedné otázce
            const allWords = area.words;
            const numQuestions = 10;

            const questions = await Promise.all(
                Array.from({ length: numQuestions }).map(async (_, i) => {
                    // Vyber správnou trojici slov
                    const correctWords = getRandomElements(allWords, NUM_SOUNDS);

                    // Získej audia a obrázky pro správnou trojici
                    const audios = correctWords.map(w => getAudioByWord(w));
                    const images = await Promise.all(correctWords.map(w => resolveImage(getImageByWordIndex(allWords.indexOf(w)))));

                    // Vygeneruj další (nesprávné) koláže
                    const options = [images];
                    const optionsWords = [correctWords];
                    const checkDuplicate = (words, existingWords) => {
                        return JSON.stringify(words) === JSON.stringify(correctWords) || existingWords.some(ow => JSON.stringify(ow) === JSON.stringify(words));
                    };
                    while (options.length < NUM_OPTIONS) {
                        let wrongWords;
                        do {
                            wrongWords = getRandomElements(allWords, NUM_SOUNDS);
                        } while (checkDuplicate(wrongWords, optionsWords));
                        const wrongImages = await Promise.all(wrongWords.map(w => resolveImage(getImageByWordIndex(allWords.indexOf(w)))));
                        options.push(wrongImages);
                        optionsWords.push(wrongWords);
                    }

                    // Zamíchej pořadí koláží i slov
                    const zipped = options.map((imgs, idx) => ({ imgs, words: optionsWords[idx] }));
                    const shuffled = shuffleArray(zipped);
                    const shuffledOptions = shuffled.map(z => z.imgs);
                    const shuffledOptionsWords = shuffled.map(z => z.words);
                    const correctIndex = shuffledOptions.findIndex(opt => JSON.stringify(opt) === JSON.stringify(images));

                    return {
                        id: i + 1,
                        audios, // pole 3 zvuků
                        options: shuffledOptions, // pole 3 koláží (každá je pole 3 obrázků)
                        optionsWords: shuffledOptionsWords, // pole 3 trojic slov
                        correctIndex,
                        correctWords,
                        userAnswer: null,
                        status: null
                    };
                })
            );
            return questions;
        }
        case 'listen': {

            const GRID_SIZE = 9;
            const allWords = area.words;
            const numQuestions = Math.min(allWords.length, 20);

            // Zamíchej pořadí správných slov, aby se neopakovaly v sérii
            const shuffledCorrectWords = shuffleArray(allWords).slice(0, numQuestions);

            const questions = await Promise.all(
                shuffledCorrectWords.map(async (correctWord, i) => {
                    // Vytvoř pool možností obsahující správné slovo
                    const otherWords = allWords.filter(w => w !== correctWord);
                    const randomOthers = getRandomElements(otherWords, Math.min(GRID_SIZE - 1, otherWords.length));
                    const optionsWords = shuffleArray([correctWord, ...randomOthers]);

                    const correctIndex = optionsWords.indexOf(correctWord);
                    const optionIndexes = optionsWords.map(w => allWords.indexOf(w));
                    const images = await Promise.all(optionIndexes.map(idx => resolveImage(getImageByWordIndex(idx))));
                    const audio = getAudioByWord(correctWord);
                    return {
                        id: i + 1,
                        options: images,
                        optionsWords: optionsWords,
                        audio,
                        correctIndex,
                        correctWord,
                        userAnswer: null,
                        status: null
                    };
                })
            );
            return questions;
        }
        case 'textCompletion': {
            const levelKey = {
                A2: 'textA2',
                B1: 'textB1',
                B2: 'textB2',
                C1: 'textC1'
            }[level?.id || 'A2'];

            let baseText = area[levelKey];
            const allWords = area.wordsEs;

            if (!baseText || !Array.isArray(allWords)) return [];

            const matches = [...baseText.matchAll(/\*(.+?)\*/g)];
            if (matches.length === 0) return [];

            const correctAnswers = {};
            const usedWords = new Set();

            for (let i = 0; i < matches.length; i++) {
                const gapId = `gap${i + 1}`;
                const raw = matches[i][1];
                const wordOnly = raw.replace(/^(el|la)\s+/i, '').trim();

                if (usedWords.has(wordOnly)) return [];

                usedWords.add(wordOnly);
                correctAnswers[gapId] = wordOnly;
                baseText = baseText.replace(matches[i][0], `{{${gapId}}}`);
            }

            const words = shuffleArray([...usedWords]);
            if (words.length !== matches.length) return [];

            return [{
                id: 1,
                text: baseText,
                words,
                correctAnswers
            }];
        }

        case 'spinner': {
            const spinnerItems = await Promise.all(
                area.words.map(async (word, index) => {
                    const image = await resolveImage(getImageByWordIndex(index));
                    const audio1 = getAudioByWord(word);

                    return {
                        id: index + 1,
                        image1: image || null,
                        audio1: audio1 || null,
                        userAnswer: null,
                        status: null,
                        correctAnswer: word,
                        correctAnswerArr: [word]
                    };
                })
            );
            //    console.log('spinnerItems', spinnerItems);
            return spinnerItems;
        }

        case 'yesNo': {
            return Promise.all(
                area.words.map(async (word, index) => {
                    let result, answer;
                    if (Math.random() < 0.5) {
                        result = true;
                        answer = word;
                    } else {
                        const otherWords = area.words.filter(w => w !== word);
                        result = false;
                        answer = getRandomElements(otherWords, 1)[0];
                    }

                    const image = await resolveImage(getImageByWordIndex(index));

                    return {
                        id: index + 1,
                        correctAnswer: result,
                        correctAnswerArr: [result],
                        question: replaceAsteriskWithNeedle(area.question1, answer),
                        image1: image || null,
                        userAnswer: null,
                        status: null
                    };
                })
            );
        }

        case '4options': {
            return Promise.all(
                area.words.map(async (word, index) => {
                    let correctWords = [word];
                    let correctAnswer = word;
                    let wrongAnswers = [];
                    let question;
                    let audio1 = null, audio2 = null, audio3 = null;
                    let image1 = null, image2 = null, image3 = null;

                    if (quiz.x3) {
                        correctWords = getRandomElements(area.words, 3);
                        correctAnswer = correctWords.join(' - ');

                        // Generuj nesprávné odpovědi s kontrolou duplicit
                        wrongAnswers = [];
                        const maxAttempts = 50;
                        let attempts = 0;

                        while (wrongAnswers.length < 3 && attempts < maxAttempts) {
                            attempts++;
                            const candidate = getRandomElements(area.words, 3);
                            const candidateStr = candidate.join(' - ');

                            // Zkontroluj, že kandidát není duplicitní
                            if (candidateStr !== correctAnswer &&
                                !wrongAnswers.includes(candidateStr) &&
                                !arrayEquals(candidate, correctWords)) {
                                wrongAnswers.push(candidateStr);
                            }
                        }

                        // Pokud se nepodařilo vygenerovat 3 unikátní špatné odpovědi, doplň pomocí fallback
                        while (wrongAnswers.length < 3) {
                            const fallback = getRandomElements(area.words, 3).join(' - ');
                            if (!wrongAnswers.includes(fallback) && fallback !== correctAnswer) {
                                wrongAnswers.push(fallback);
                            }
                        }

                        [image1, image2, image3] = correctWords.map(w => getImageByWordIndex(area.words.indexOf(w)));
                        [audio1, audio2, audio3] = correctWords.map(w => getAudioByWord(w));

                        question = replaceAsteriskWithNeedle(area.question4 ?? area.question2, word);
                    } else {
                        const otherWords = area.words.filter(w => w !== word);
                        wrongAnswers = getRandomElements(otherWords, 3);
                        question = replaceAsteriskWithNeedle(area.question2, word);
                        audio1 = getAudioByWord(word);
                        image1 = getImageByWordIndex(index);
                    }

                    [image1, image2, image3] = await Promise.all(
                        [image1, image2, image3].map(resolveImage)
                    );

                    const options = shuffleArray([correctAnswer, ...wrongAnswers]);

                    return {
                        id: index + 1,
                        question,
                        correctAnswer,
                        correctAnswerArr: correctWords,
                        options,
                        image1,
                        image2,
                        image3,
                        audio1,
                        audio2,
                        audio3,
                        userAnswer: null,
                        status: null
                    };
                })
            );
        }

        case 'writtenAnswer': {
            return Promise.all(
                area.words.map(async (word, index) => {
                    let correctAnswer = word;
                    let correctAnswerWords = [word];
                    let question;
                    let audio1 = null, audio2 = null, audio3 = null;
                    let image1 = null, image2 = null, image3 = null;

                    if (quiz.x3) {
                        // Ujisti se, že vybereme 3 různé obrázky
                        const correctImages = getRandomElements(area.wordsEs, Math.min(3, area.wordsEs.length));
                        correctAnswerWords = correctImages.map(img => area.words[area.wordsEs.indexOf(img)]);
                        correctAnswer = correctAnswerWords.join(' - ');
                        question = replaceAsteriskWithNeedle(area.question5 || area.question3, word);

                        [image1, image2, image3] = correctImages.map(img => getImageByWordIndex(area.wordsEs.indexOf(img)));
                        [audio1, audio2, audio3] = correctAnswerWords.map(w => getAudioByWord(w));
                    } else {
                        question = replaceAsteriskWithNeedle(area.question3, word);
                        image1 = getImageByWordIndex(index);
                        audio1 = getAudioByWord(word);
                    }

                    [image1, image2, image3] = await Promise.all(
                        [image1, image2, image3].map(resolveImage)
                    );

                    return {
                        id: index + 1,
                        correctAnswer,
                        correctAnswerArr: correctAnswerWords,
                        question,
                        image1,
                        image2,
                        image3,
                        audio1,
                        audio2,
                        audio3,
                        userAnswer: null,
                        status: null
                    };
                })
            );
        }
        default:
            return [];
    }
};



export const getImageThumbnail = async (image) => {
    if (typeof image === 'string') return image;
    const thumbnail = image?.thumbnail;
    if (typeof thumbnail?.url === 'string'
        && Number(thumbnail.height) === Number(thumbHeight)
        && Number(thumbnail.quality) === Number(thumbQuality)) {
        return thumbnail.url;
    }
    if (image?._id) {
        try {
            const data = await fetchCached(
                `assets/image/${image._id}?m=thumbnail&h=${thumbHeight}&q=${thumbQuality}&o=0`,
                60 * 60 * 1000,
            );
            return typeof data === 'string' ? data : data?.path || null;

        } catch (error) {
            //    console.error(`Error fetching data from ${apiUrl}assets/image/${image._id}`, error);
            return null;
        }
    }
    return image?.path || null;
};



export const fetchTranslationsAll = async () => {
    const languageTranslations = await Promise.all(availableLanguages.map(async (lang) => {
        const response = toItems(await fetchData(`content/items/translations?locale=${lang.code}`));
        const translation = {};

        response.forEach(item => {
            translation[item.key] = item.value;
        });

        return [lang.code, { translation }];
    }));

    return languageTranslations.reduce((translations, [code, resource]) => {
        translations[code] = resource;
        return translations;
    }, {});
};



export const fetchQuizTypes = async (language, area) => {

    if (!language.code) {
        return [];
    }
    const response = toItems(await fetchData(`content/items/quizTypes?locale=`+language.code));

    const quizzes = response.map(quiz => ({
        id: quiz._id,
        name: quiz.name,
        name_alt: quiz.name_alt,
        number: quiz.number,
        type: quiz.type,
        x3: quiz.x3
    }));

    return quizzes;
};

/**
 Vytáhle uplný seznam všech oblastí, včetně obrázků
 - pole s obrázky předělá do formátu např: dog: {}
 - pole wordsEs - z anglické lokalizace - kvůli párování obrázků k otázce
 */
export const fetchGroups = async (language) => {
    if (!language?.code) {
        return [];
    }

    const cached = readSelection(groupCacheKey(language), CONTENT_TTL);
    if (cached !== null) return cached;
    const payload = await fetchData(`content/items/groups?locale=${language.code}`);
    if (payload === null) return getCachedGroups(language) || [];
    const response = toItems(payload);
    /*   let responseEs;
       if (language.code !== 'es') {
           responseEs = await fetchData(`content/items/groups?locale=es`);
       } else {
           responseEs = response;
       }*/

    // Resolve thumbnails in the cards so one slow image cannot block the list.
    const groups = response.map((group) => ({
        id: group._id,
        name: group.name,
        image: group.image,
    }));

    // Seřazení skupin
    const sortedGroups = groups.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

    saveSelection(groupCacheKey(language), sortedGroups);
    return sortedGroups;
};




/**
 Vytáhle uplný seznam všech oblastí, včetně obrázků
 - pole s obrázky předělá do formátu např: dog: {}
 - pole wordsEs - z anglické lokalizace - kvůli párování obrázků k otázce
 */
export const fetchAreas = async (language, group) => {
    if (!language?.code || !group?.id) {
        return [];
    }

    const cached = readSelection(areaCacheKey(language, group), CONTENT_TTL);
    if (cached !== null) return cached;


    const normalizeString = (str) => {
        return str
            ?.normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035\u0060\u00B4\u02BB\u02BC\u02BD\u02BE\u02BF\u275B\u275C\uFF07]/g, "'")
            .replace(/[^a-zA-Z0-9']/g, ' ')
            .replace(/\s+/g, ' ')
            .toLowerCase()
            .trim();
    };

    function formatImagesArray(images) {
        if (!Array.isArray(images)) {
            return {};
        }

        return images.reduce((acc, image, index) => {
            if (!image || !image.title) {
                console.warn(`⚠️ Image at index ${index} is invalid`, image);
                return acc;
            }

            const key = normalizeString(image.title);
            acc[key] = image;
            return acc;
        }, {});
    }

    // Načtení dat podle jazyka
    const filter = encodeURIComponent(JSON.stringify({ 'group._id': group.id }));
    const areaUrl = (locale) => `content/items/areas?locale=${locale}&filter=${filter}`;
    // Keep localized fields: Cockpit field projection can discard their translations.
    const [localized, spanish] = await Promise.all([
        fetchData(areaUrl(language.code)),
        language.code === 'es' ? null : fetchData(areaUrl('es')),
    ]);
    if (localized === null) return getCachedAreas(language, group) || [];
    const response = toItems(localized);
    const responseEs = language.code === 'es' ? response : toItems(spanish);
    const spanishById = new Map(responseEs.map((area) => [area._id, area]));


    //     console.log('fetchAreas response', response);

    // Zpracování oblastí s asynchronními operacemi pro klíč `image`
    const areas = response
        .filter((area) => String(group.id) === String(area.group?._id))
        .map((area) => {
            // Nahrazení ň za ñ pro španělštinu
            const words = (area.words || []).map((word) =>
                language.code === 'es' ? word.replace(/ň/g, 'ñ') : word
            );

            const audioMapRaw = formatImagesArray(area.audio);
            const audioOrdered = {};
            for (const word of words) {
                const key = normalizeString(word);
                const audio = audioMapRaw[key] || null;
                audioOrdered[key] = audio ? { ...audio, word } : null;
            }


            return {
                id: area._id,
                name: area.name,
                image: area.image,
                images: formatImagesArray(area.images),
                video: area.video,
                audio: audioOrdered,
                audio10: area.audio10,
                answers10: area.answers10,
                question1: area.question1,
                question2: area.question2,
                question3: area.question3,
                question4: area.question4,
                question5: area.question5,
                words: words,
                wordsEs: language.code === 'es' ? words : spanishById.get(area._id)?.words,
                wordsCount: words.length,
                textA2: area.textA2,
                textB1: area.textB1,
                textB2: area.textB2,
                textC1: area.textC1
            };
        });

    // Filtrování null hodnot
    const filteredAreas = areas.filter((area) => area !== null);

    // Seřazení oblastí
    const sortedAreas = filteredAreas.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
    if (language.code === 'es' || spanish !== null) saveSelection(areaCacheKey(language, group), sortedAreas);
    return sortedAreas;
};





export const fetchLanguages = async () => {
    return availableLanguages;
};


// Cockpit API
export const fetchData = async (url) => {
    try {
        return await fetchCached(url);

    } catch (error) {
        console.error(`Error fetching data from ${apiUrl}${url}:`, error);
        return null;
    }
};



